import { create } from 'zustand';
import { toast } from 'react-toastify';
import { api, registerAccessTokenGetter } from '../api/client';
import i18n from '../i18n';

interface AuthState {
  // Access token lives in MEMORY only. It is never written to localStorage —
  // an XSS payload cannot exfiltrate it from storage, and it is short-lived.
  // The refresh token lives exclusively in an HttpOnly cookie (set by the
  // backend at login) and is never visible to JS at all.
  token: string | null;
  user: {
    id: string;
    email: string;
    username: string;
    full_name: string;
  } | null;
  originalUser: {
    id: string;
    email: string;
    username: string;
    full_name: string;
  } | null;
  isImpersonating: boolean;
  setToken: (token: string) => void;
  setUser: (user: AuthState['user']) => void;
  setOriginalUser: (user: AuthState['originalUser']) => void;
  setIsImpersonating: (isImpersonating: boolean) => void;
  impersonate: (token: string, user: AuthState['user'], originalUser: AuthState['originalUser']) => void;
  stopImpersonating: () => Promise<void>;
  logout: () => Promise<void>;
  isAuthenticated: () => boolean;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  token: null,
  user: null,
  originalUser: (() => {
    if (typeof window === 'undefined') return null;
    const stored = localStorage.getItem('originalUser');
    if (!stored) return null;
    try {
      return JSON.parse(stored);
    } catch {
      // Corrupted value must not white-screen the app — drop it and continue.
      localStorage.removeItem('originalUser');
      return null;
    }
  })(),
  isImpersonating: typeof window !== 'undefined' ? localStorage.getItem('isImpersonating') === 'true' : false,
  setToken: (token: string) => {
    set({ token });
  },
  setUser: (user: AuthState['user']) => set({ user }),
  setOriginalUser: (user: AuthState['originalUser']) => set({ originalUser: user }),
  setIsImpersonating: (isImpersonating: boolean) => {
    set({ isImpersonating });
    if (isImpersonating) {
      localStorage.setItem('isImpersonating', 'true');
    } else {
      localStorage.removeItem('isImpersonating');
    }
  },
  impersonate: (token: string, user: AuthState['user'], originalUser: AuthState['originalUser']) => {
    // Refresh token rotated into the HttpOnly cookie by the backend; only the
    // in-memory access token + non-secret UI flags are kept here.
    set({ token, user, originalUser, isImpersonating: true });
    localStorage.setItem('isImpersonating', 'true');
    if (originalUser) {
      localStorage.setItem('originalUser', JSON.stringify(originalUser));
    }
  },
  stopImpersonating: async () => {
    try {
      const response = await api.post('/auth/stop-impersonation');
      const { token, user } = response.data.data;

      set({ token, user, originalUser: null, isImpersonating: false });
      localStorage.removeItem('isImpersonating');
      localStorage.removeItem('originalUser');

      window.location.reload();
    } catch (error: any) {
      // SECURITY: do NOT clear local impersonation state here and do NOT fall
      // through to a full logout(). A failed stop-impersonation call (network
      // error, 5xx, or the backend revoke itself failing) means the session is
      // still impersonating server-side — silently clearing local state (or
      // logging the admin out entirely) would let the UI claim "stopped" while
      // the impersonated session keeps working. Keep state intact and surface
      // the failure so the admin knows to retry.
      toast.error(error.response?.data?.error || i18n.t('pages:header.stopImpersonationError'));
    }
  },
  logout: async () => {
    // SECURITY: local auth state MUST be cleared no matter what — server
    // revocation is a best-effort side concern. If the revoke call throws, we
    // must not leave the app believing it is still authenticated. The `finally`
    // guarantees the clear runs. The backend /auth/logout also expires the
    // HttpOnly refresh cookie.
    try {
      await api.post('/auth/logout');
    } catch {
      // Ignore errors — we're logging out regardless.
    } finally {
      set({ token: null, user: null, originalUser: null, isImpersonating: false });
      localStorage.removeItem('isImpersonating');
      localStorage.removeItem('originalUser');
      window.location.href = '/login';
    }
  },
  isAuthenticated: () => {
    return !!get().token;
  },
}));

// Expose the in-memory access token to the axios request interceptor without a
// static import cycle (client.ts cannot statically import this store).
registerAccessTokenGetter(() => useAuthStore.getState().token);

// Helper hook for authentication check
export const useIsAuthenticated = () => {
  return useAuthStore((state) => !!state.token);
};
